import {
    OG_IMAGE_ALT,
    SITE_OG_DESCRIPTION,
    PROFILE_OG_IMAGE_ALT,
    SIXES_PAGE_OG_DESCRIPTION,
    SIXES_PAGE_OG_TITLE,
    SQUARES_PAGE_OG_DESCRIPTION,
    SQUARES_PAGE_OG_TITLE,
    BULLION_PAGE_OG_DESCRIPTION,
    BULLION_PAGE_OG_TITLE,
    TRADER_PAGE_OG_DESCRIPTION,
    TRADER_PAGE_OG_TITLE,
    LEAGUES_PAGE_OG_DESCRIPTION,
    LEAGUES_PAGE_OG_TITLE,
    GAMES_PAGE_OG_TITLE,
    GAMES_PAGE_OG_DESCRIPTION,
    ABOUT_PAGE_OG_TITLE,
    ABOUT_PAGE_OG_DESCRIPTION,
    HOW_TO_PLAY_PAGE_OG_TITLE,
    HOW_TO_PLAY_PAGE_OG_DESCRIPTION,
    CONTACT_PAGE_OG_TITLE,
    CONTACT_PAGE_OG_DESCRIPTION,
    XP_LEADERBOARD_PAGE_OG_TITLE,
    XP_LEADERBOARD_PAGE_OG_DESCRIPTION,
    MATCHES_PAGE_OG_TITLE,
    MATCHES_PAGE_OG_DESCRIPTION,
    RESULTS_PAGE_OG_TITLE,
    RESULTS_PAGE_OG_DESCRIPTION,
    SQUARES_RULES_OG_TITLE,
    SQUARES_RULES_OG_DESCRIPTION,
    buildProfileInviteShareTitle,
    buildProfileOgDescription,
} from "./shareCopy";
import type { OgShareGame } from "./shareOgImage";
import type { LeagueResultsShareSnapshot } from "../components/Leagues/lib/leagueResultsShare";
import {
    buildLeagueResultsOgDescription,
    buildLeagueResultsShareTitle,
} from "../components/Leagues/lib/leagueResultsShare";

export type SharePageJsonLdApplication = {
    name: string;
    description: string;
    /** Defaults to the page canonical URL when omitted. */
    url?: string;
};

export type SharePageJsonLdFaqItem = {
    question: string;
    answer: string;
};

export type SharePageMeta = {
    title: string;
    description: string;
    imageAlt: string;
    /** OG/Twitter preview image for this game (CDN). */
    ogGame?: OgShareGame;
    /** Absolute override for og:image (e.g. dynamic league results card). */
    imageUrl?: string;
    /** Optional canonical URL override for helmet. */
    canonicalUrl?: string;
    /** Comma-separated meta keywords for match discoverability. */
    keywords?: string;
    /** Plain match name for JSON-LD (e.g. "India vs Australia"). */
    jsonLdMatchName?: string;
    /** Lobby / product SoftwareApplication JSON-LD (used when no match event). */
    jsonLdApplication?: SharePageJsonLdApplication;
    /** FAQPage JSON-LD (emitted alongside other structured data). */
    jsonLdFaq?: SharePageJsonLdFaqItem[];
    /** robots meta content (e.g. `noindex, nofollow`). */
    robots?: string;
};

export function buildMatchSeoKeywords(matchLabel: string, game: string): string {
    const label = String(matchLabel ?? "").trim();
    const parts = ["cricket8", "cricket", "live cricket", game];
    if (!label) return parts.join(", ");
    parts.push(label);
    const vs = label.split(/\s+v\s+/i);
    if (vs.length >= 2) {
        const a = vs[0].trim();
        const b = vs[1].trim();
        if (a) parts.push(a, `${a} cricket`);
        if (b) parts.push(b, `${b} cricket`);
        if (a && b) parts.push(`${a} vs ${b}`, `${a} v ${b}`);
    }
    return [...new Set(parts.map((p) => p.trim()).filter(Boolean))].join(", ");
}

export function formatTeamsVs(teams?: readonly string[] | null): string {
    const a = String(teams?.[0] ?? "").trim();
    const b = String(teams?.[1] ?? "").trim();
    if (a && b) return `${a} vs ${b}`;
    return "";
}

/** Cricket-style share label: `INDIA v WEST INDIES`. */
export function formatTeamsVUpper(teams?: readonly string[] | null): string {
    const a = String(teams?.[0] ?? "").trim();
    const b = String(teams?.[1] ?? "").trim();
    if (a && b) return `${a} v ${b}`.toUpperCase();
    return "";
}

export function resolveMatchLabel(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
}): string {
    const vs = formatTeamsVs(args.teams);
    if (vs) return vs;
    return String(args.matchTitle ?? args.matchName ?? "").trim();
}

/** Uppercase cricket `v` label for invite share text (teams preferred, else title/name). */
export function resolveLeagueInviteMatchLabel(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
}): string {
    const fromTeams = formatTeamsVUpper(args.teams);
    if (fromTeams) return fromTeams;
    const raw = String(args.matchTitle ?? args.matchName ?? "").trim();
    if (!raw) return "";
    return raw.replace(/\s+vs\.?\s+/i, " v ").toUpperCase();
}

export function resolveTraderSportLabel(sport?: string | null): string {
    const sportRaw = String(sport ?? "cricket").trim().toLowerCase();
    if (sportRaw === "cricket") return "cricket";
    if (sportRaw === "football") return "football";
    if (sportRaw === "tennis") return "tennis";
    if (sportRaw === "horses") return "horse racing";
    return sportRaw;
}

export function buildSquaresMatchShareMeta(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    tournament?: string | null;
}): SharePageMeta {
    const match = resolveMatchLabel(args);
    const comp = String(args.tournament ?? "").trim();
    const title = match ? `${match} | Squares on Cricket8` : "Squares Match Play | Cricket8";
    const compBit = comp ? ` in ${comp}` : "";
    const description = match
        ? `Play Squares on Cricket8 for ${match}${compBit}. Pick runs on every ball of the live over and compete with friends.`
        : SITE_OG_DESCRIPTION;
    return {
        title,
        description,
        imageAlt: match ? `Cricket8 Squares — ${match}` : OG_IMAGE_ALT,
        ogGame: "squares",
        keywords: buildMatchSeoKeywords(match, "squares"),
        jsonLdMatchName: match || undefined,
    };
}

export function buildTraderMatchShareMeta(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
    tournament?: string | null;
    sport?: string | null;
}): SharePageMeta {
    const match = resolveMatchLabel(args);
    const sportLabel = resolveTraderSportLabel(args.sport);
    const title = match
        ? `${match} | Live ${sportLabel} games on Cricket8`
        : TRADER_PAGE_OG_TITLE;
    const comp = String(args.tournament ?? "").trim();
    const compBit = comp ? ` in ${comp}` : "";
    const description = match
        ? `Follow ${match}${compBit} on Cricket8 — live ${sportLabel} games.`
        : SITE_OG_DESCRIPTION;
    return {
        title,
        description,
        imageAlt: match ? `Cricket8 — ${match}` : OG_IMAGE_ALT,
        ogGame: "trader",
        keywords: buildMatchSeoKeywords(match, "trader"),
        jsonLdMatchName: match || undefined,
    };
}

export function buildBullionContestShareMeta(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
    contestName?: string | null;
    contestKind?: "arena" | "battle" | null;
}): SharePageMeta {
    const match = resolveMatchLabel({
        teams: args.teams,
        matchTitle: args.matchTitle,
        matchName: args.matchName,
    });
    const contestName = String(args.contestName ?? "").trim();

    if (contestName) {
        const kindLabel = args.contestKind === "battle" ? "Bullion Battle" : "Bullion Arena";
        const title = `${contestName} | ${kindLabel} on Cricket8`;
        const matchBit = match ? ` for ${match}` : "";
        const description = `Join ${contestName}${matchBit} on Cricket8 Bullion — pick team, innings, player and match and compete with friends.`;
        return {
            title,
            description,
            imageAlt: `Cricket8 Bullion — ${contestName}`,
            ogGame: "bullion",
            keywords: buildMatchSeoKeywords(match || contestName, "bullion"),
            jsonLdMatchName: match || contestName,
        };
    }

    const title = match ? `${match} | Cricket8 Bullion` : "Bullion on Cricket8";
    const description = match
        ? `Play Bullion on Cricket8 for ${match} — public arenas and private battles. Pick your board and compete with friends.`
        : SITE_OG_DESCRIPTION;
    return {
        title,
        description,
        imageAlt: match ? `Cricket8 Bullion — ${match}` : OG_IMAGE_ALT,
        ogGame: "bullion",
        keywords: buildMatchSeoKeywords(match, "bullion"),
        jsonLdMatchName: match || undefined,
    };
}

export function buildProfileShareMeta(args: {
    displayName?: string | null;
    profileUrl?: string;
} = {}): SharePageMeta {
    const name = String(args.displayName ?? "").trim();
    const title = buildProfileInviteShareTitle(name || null);
    const description = buildProfileOgDescription(name || null);
    return {
        title,
        description,
        imageAlt: name ? `${name} on Cricket8` : PROFILE_OG_IMAGE_ALT,
        ogGame: "site",
    };
}

function buildGameLobbyApplication(name: string, description: string, path: string): SharePageJsonLdApplication {
    return {
        name,
        description,
        url: `https://cricket8.com${path}`,
    };
}

export function buildSquaresLobbyShareMeta(args: { jsonLdFaq?: SharePageJsonLdFaqItem[] } = {}): SharePageMeta {
    return {
        title: SQUARES_PAGE_OG_TITLE,
        description: SQUARES_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 Squares",
        ogGame: "squares",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Squares", SQUARES_PAGE_OG_DESCRIPTION, "/squares"),
        ...(args.jsonLdFaq?.length ? { jsonLdFaq: args.jsonLdFaq } : {}),
    };
}

export function buildBullionLobbyShareMeta(): SharePageMeta {
    return {
        title: BULLION_PAGE_OG_TITLE,
        description: BULLION_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 Bullion",
        ogGame: "bullion",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Bullion", BULLION_PAGE_OG_DESCRIPTION, "/bullion"),
    };
}

export function buildTraderLobbyShareMeta(): SharePageMeta {
    return {
        title: TRADER_PAGE_OG_TITLE,
        description: TRADER_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "trader",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Trader", TRADER_PAGE_OG_DESCRIPTION, "/trader"),
    };
}

export function buildSixesLobbyShareMeta(): SharePageMeta {
    return {
        title: SIXES_PAGE_OG_TITLE,
        description: SIXES_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 Sixes",
        ogGame: "sixes",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Sixes", SIXES_PAGE_OG_DESCRIPTION, "/sixes"),
    };
}

export function buildAllGamesShareMeta(): SharePageMeta {
    return {
        title: GAMES_PAGE_OG_TITLE,
        description: GAMES_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "site",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Games", GAMES_PAGE_OG_DESCRIPTION, "/all-games"),
    };
}

export function buildAboutShareMeta(args: { jsonLdFaq?: SharePageJsonLdFaqItem[] } = {}): SharePageMeta {
    return {
        title: ABOUT_PAGE_OG_TITLE,
        description: ABOUT_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "site",
        ...(args.jsonLdFaq?.length ? { jsonLdFaq: args.jsonLdFaq } : {}),
    };
}

export function buildHowToPlayShareMeta(): SharePageMeta {
    return {
        title: HOW_TO_PLAY_PAGE_OG_TITLE,
        description: HOW_TO_PLAY_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "site",
    };
}

export function buildContactShareMeta(): SharePageMeta {
    return {
        title: CONTACT_PAGE_OG_TITLE,
        description: CONTACT_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "site",
    };
}

export function buildXpLeaderboardShareMeta(): SharePageMeta {
    return {
        title: XP_LEADERBOARD_PAGE_OG_TITLE,
        description: XP_LEADERBOARD_PAGE_OG_DESCRIPTION,
        imageAlt: OG_IMAGE_ALT,
        ogGame: "site",
    };
}

export function buildMatchesLobbyShareMeta(): SharePageMeta {
    return {
        title: MATCHES_PAGE_OG_TITLE,
        description: MATCHES_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 matches, fixtures and live scores",
        ogGame: "results",
    };
}

export function buildResultsLobbyShareMeta(): SharePageMeta {
    return {
        title: RESULTS_PAGE_OG_TITLE,
        description: RESULTS_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 cricket results, scores and match outcomes",
        ogGame: "results",
    };
}

export function buildSquaresRulesShareMeta(args: { jsonLdFaq?: SharePageJsonLdFaqItem[] } = {}): SharePageMeta {
    return {
        title: SQUARES_RULES_OG_TITLE,
        description: SQUARES_RULES_OG_DESCRIPTION,
        imageAlt: "Cricket8 Squares rules",
        ogGame: "squares",
        ...(args.jsonLdFaq?.length ? { jsonLdFaq: args.jsonLdFaq } : {}),
    };
}

export function buildSixesMatchShareMeta(args: {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
}): SharePageMeta {
    const match = resolveMatchLabel(args);
    const title = match ? `${match} | Sixes on Cricket8` : "Sixes Match | Cricket8";
    const description = match
        ? `Play Sixes on Cricket8 for ${match} — live match cricket cards and multiply coins.`
        : SIXES_PAGE_OG_DESCRIPTION;
    return {
        title,
        description,
        imageAlt: match ? `Cricket8 Sixes — ${match}` : "Cricket8 Sixes",
        ogGame: "sixes",
        keywords: buildMatchSeoKeywords(match, "sixes"),
        jsonLdMatchName: match || undefined,
    };
}

export function buildLeaguesLobbyShareMeta(args: { jsonLdFaq?: SharePageJsonLdFaqItem[] } = {}): SharePageMeta {
    return {
        title: LEAGUES_PAGE_OG_TITLE,
        description: LEAGUES_PAGE_OG_DESCRIPTION,
        imageAlt: "Cricket8 Leagues",
        ogGame: "leagues",
        jsonLdApplication: buildGameLobbyApplication("Cricket8 Leagues", LEAGUES_PAGE_OG_DESCRIPTION, "/leagues"),
        ...(args.jsonLdFaq?.length ? { jsonLdFaq: args.jsonLdFaq } : {}),
    };
}

/** Join / detail share meta for `/leagues?code=` and `/leagues?id=` links. */
export function buildLeagueInviteShareMeta(args: {
    leagueName?: string | null;
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
    /** Whoever sent the link (`invitedBy`) — never the host, who usually isn't the sender. */
    inviterName?: string | null;
} = {}): SharePageMeta {
    const leagueName = String(args.leagueName ?? "").trim();
    const match = resolveMatchLabel(args);
    const inviter = String(args.inviterName ?? "").trim();
    const leagueLabel = leagueName ? `"${leagueName}"` : "a Cricket8 league";

    if (inviter) {
        const matchBit = match ? ` for ${match}` : "";
        return {
            title: `${inviter} invited you to ${leagueLabel}`,
            description: `Join ${inviter} in ${leagueLabel}${matchBit} on Cricket8 Leagues — play the match against friends.`,
            imageAlt: `${inviter} — Cricket8 Leagues`,
            ogGame: "leagues",
            keywords: buildMatchSeoKeywords(match || leagueName, "leagues"),
            jsonLdMatchName: match || undefined,
        };
    }

    if (leagueName && match) {
        return {
            title: `${leagueName} | ${match} | Leagues on Cricket8`,
            description: `Join "${leagueName}" for ${match} on Cricket8 Leagues — private tables with friends, ranked on the leaderboard.`,
            imageAlt: `Cricket8 Leagues — ${leagueName}`,
            ogGame: "leagues",
            keywords: buildMatchSeoKeywords(match, "leagues"),
            jsonLdMatchName: match,
        };
    }

    if (leagueName) {
        return {
            title: `${leagueName} | Leagues on Cricket8`,
            description: `Join "${leagueName}" on Cricket8 Leagues — private tables with friends, pick which games to play, and battle for the leaderboard.`,
            imageAlt: `Cricket8 Leagues — ${leagueName}`,
            ogGame: "leagues",
            keywords: buildMatchSeoKeywords(leagueName, "leagues"),
        };
    }

    if (match) {
        return {
            title: `${match} | Leagues on Cricket8`,
            description: `Play Leagues on Cricket8 for ${match} — private tables with friends, ranked on the leaderboard.`,
            imageAlt: `Cricket8 Leagues — ${match}`,
            ogGame: "leagues",
            keywords: buildMatchSeoKeywords(match, "leagues"),
            jsonLdMatchName: match,
        };
    }

    return buildLeaguesLobbyShareMeta();
}

/** Finished-league results card (`/leagues?r=`). */
export function buildLeagueResultsShareMeta(
    snapshot: LeagueResultsShareSnapshot,
    args: { url?: string; imageUrl?: string } = {},
): SharePageMeta {
    const title = buildLeagueResultsShareTitle(snapshot);
    const description = buildLeagueResultsOgDescription(snapshot);
    return {
        title,
        description,
        imageAlt: title,
        ogGame: "leagues",
        ...(args.imageUrl ? { imageUrl: args.imageUrl } : {}),
        ...(args.url ? { canonicalUrl: args.url } : {}),
        keywords: buildMatchSeoKeywords(snapshot.matchLabel || snapshot.leagueName, "leagues"),
        jsonLdMatchName: snapshot.matchLabel || undefined,
    };
}
